import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiChip,
  MiDetailPageHeader,
  MiLink,
  MiList,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  LANGUAGES,
  PAGINATION,
  USER_ACTIVITY_STATUSES,
} from "@miplatform/constants";
import { mdiChevronLeft, mdiEmailSync, mdiPlus } from "@mdi/js";
import { useSession } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { AddUserDrawer } from "./AddUserDrawer/AddUserDrawer";
import { useManageUserViewModel } from "./useManageUserViewModel";

export function ManageUser() {
  const {
    session: { tenantId },
  } = useSession();
  const { classes, theme } = useStyles();
  const {
    users,
    stateManager,
    roles,
    search,
    drawer,
    debouncedSearch,
    setSearch,
    resendInvitation,
    setDrawer,
    onCreateUser,
    getChipColor,
    loadData,
    onSearch,
  } = useManageUserViewModel({ tenantId });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    onSearch(debouncedSearch);
  }, [debouncedSearch]);

  const userList = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: (p) => (
        <MiLink
          key={p.row.id}
          to={`/settings/user-management/users/${p.row.id}`}
          title={p.value}
          state={{ roles }}
        />
      ),
      valueGetter: (_, row) => {
        return `${row?.firstName} ${row?.lastName}`;
      },
      groupable: false,
    },
    {
      field: "displayName",
      headerName: "Display Name",
      headerAlign: "left",
      sortable: false,
      align: "left",
      flex: 1,
      groupable: false,
    },
    {
      field: "userName",
      headerName: "User Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      groupable: false,
    },
    {
      field: "title",
      headerName: "Title",
      headerAlign: "left",
      align: "left",
      flex: 1,
      groupable: false,
    },
    {
      field: "email",
      headerName: "Email",
      headerAlign: "left",
      align: "left",
      flex: 1,
      groupable: false,
    },
    {
      field: "roleNames",
      headerName: "Role(s)",
      headerAlign: "left",
      align: "left",
      flex: 1,
      sortable: false,
      groupable: false,
      valueGetter: (_, row) => {
        return row?.roles?.map((role) => role.name).join(", ");
      },
    },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "left",
      sortable: false,
      align: "left",
      groupable: false,
      renderCell: (p) => (
        <MiChip color={getChipColor(p.value)} label={p.value} />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "left",
      align: "left",
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: ({ row }) => (
        <MiButton
          title="Resend Invite"
          variant="outlined"
          size="small"
          disabled={row?.status !== USER_ACTIVITY_STATUSES.PENDING}
          startIcon={{
            path: mdiEmailSync,
          }}
          color={BUTTON_COLORS.PRIMARY}
          onClick={() => {
            resendInvitation(row);
          }}
          data-testid="resend-invite-button"
        />
      ),
    },
  ];

  return (
    <div className={classes.contentContainer}>
      <MiDetailPageHeader
        title="Users"
        titleIcon={mdiChevronLeft}
        search={search}
        onHandleSearch={(e) => setSearch(e.target.value)}
        loading={stateManager.isBusy()}
        actionComponent={
          <MiButton
            title="Add User"
            variant="contained"
            startIcon={{
              path: mdiPlus,
              iconSize: 0.8,
            }}
            data-testid="add-role-btn"
            onClick={() =>
              setDrawer({
                type: "add",
                visible: true,
                header: "Add User",
                onClick: onCreateUser,
              })
            }
            style={{
              backgroundColor: theme.palette.primary.main,
            }}
          />
        }
      />
      <Box className={classes.container}>
        <MiList
          columns={userList}
          data={users}
          paginationMode={PAGINATION.CLIENT}
          loading={stateManager.isBusy()}
          data-testid="users-table"
        />
      </Box>
      <AddUserDrawer
        open={drawer.type}
        onClose={() => setDrawer({})}
        type={drawer.type}
        header={drawer.header}
        onSubmit={drawer.onClick}
        languageOptions={LANGUAGES}
        loading={stateManager.isBusy()}
        data-testid="header"
        roles={roles}
      />
    </div>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    height: "inherit",
    overflowY: "auto",
    margin: 5,
    borderRadius: 10,
    padding: 5,
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    flexDirection: "column",
    width: "100%",
  },
  link: {
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
    textAlign: "center",
    fontWeight: theme.fontWeight.medium,
    color: theme.palette.primary.main,
  },
}));
